import {
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import React from "react";
import {
  SalespersonActivityStats,
  SegmentKey,
} from "../../../store/services/salesManagerService";
import { WorkItemTypeEnum } from "../../../store/types/work.types";
import {
  getWorkItemColor,
  getWorkItemIcon,
  getWorkItemTypeName,
} from "../../../utils/workItemUtils";
import SegmentDonutChart from "../charts/SegmentDonutChart";
import SubtypeProgressList from "../charts/SubtypeProgressList";
import {
  BACKLOG_TOOLTIP,
  CATEGORY_LABELS,
  formatPercent,
  getCompletionRateColor,
  getContactAttemptCount,
  getGrandTotal,
  getSegmentTotal,
  SUBTYPE_ORDER_COMMUNICATION,
  SUBTYPE_ORDER_FOLLOWUP,
  OVERDUE_TOOLTIP,
  SEGMENT_LABELS,
  SEGMENT_ORDER,
} from "../shared/breakdown";

interface SalespersonDetailDrawerProps {
  open: boolean;
  stats: SalespersonActivityStats | null;
  onClose: () => void;
  onOpenFullProfile?: (salespersonId: number) => void;
}

const SegmentSummaryRow: React.FC<{
  segmentKey: SegmentKey;
  stats: SalespersonActivityStats;
}> = ({ segmentKey, stats }) => {
  const theme = useTheme();
  const label = SEGMENT_LABELS[segmentKey];
  const segment = getSegmentTotal(stats.Breakdown, segmentKey);
  const category = stats.Breakdown[segmentKey];
  const comm = category.Communication.Total;
  const followup = category.Followup.Total;

  // Tomma segment döljs helt — drawern har redan 8 potentiella rader och
  // vi vill inte fylla skärmen med "Inga aktiviteter"-placeholders.
  if (segment.Total === 0) return null;

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{
            color: getCompletionRateColor(segment.CompletionRate, theme),
          }}
        >
          {formatPercent(segment.CompletionRate)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={segment.CompletionRate}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 1,
          bgcolor: alpha(theme.palette.grey[300], 0.3),
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            bgcolor: getCompletionRateColor(segment.CompletionRate, theme),
          },
        }}
      />
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          Totalt: <strong>{segment.Total}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Slutfört: <strong>{segment.Completed}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {CATEGORY_LABELS.Communication}: <strong>{comm.Total}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {CATEGORY_LABELS.Followup}: <strong>{followup.Total}</strong>
        </Typography>
      </Stack>
    </Box>
  );
};

const SalespersonDetailDrawer: React.FC<SalespersonDetailDrawerProps> = ({
  open,
  stats,
  onClose,
  onOpenFullProfile,
}) => {
  const theme = useTheme();

  if (!stats) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box sx={{ width: 520, p: 3 }} />
      </Drawer>
    );
  }

  const totals = getGrandTotal(stats.Breakdown);
  const rateColor = getCompletionRateColor(totals.CompletionRate, theme);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={stats.SalespersonImagePath}
              sx={{
                width: 64,
                height: 64,
                bgcolor: theme.palette.primary.main,
                border: `3px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              {stats.SalespersonName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {stats.SalespersonName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.TeamName || "Inget team"}
              </Typography>
              {stats.AverageCompletionTimeDays !== null &&
                stats.AverageCompletionTimeDays !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    ⌀ slutförandetid:{" "}
                    {stats.AverageCompletionTimeDays.toFixed(1)} dagar
                  </Typography>
                )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Huvud-KPI */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: alpha(rateColor, 0.08),
            border: `1px solid ${alpha(rateColor, 0.2)}`,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" fontWeight="medium">
              Slutförandegrad (total)
            </Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ color: rateColor }}>
              {formatPercent(totals.CompletionRate)}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={totals.CompletionRate}
            sx={{
              height: 8,
              borderRadius: 4,
              mb: 1.5,
              bgcolor: alpha(theme.palette.grey[300], 0.3),
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                bgcolor: rateColor,
              },
            }}
          />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Stat label="Totalt" value={totals.Total} />
            <Stat
              label="Slutförda"
              value={totals.Completed}
              color={theme.palette.success.main}
            />
            <Stat
              label="Aktiva"
              value={totals.Active}
              color={theme.palette.info.main}
            />
            <Tooltip title={OVERDUE_TOOLTIP} arrow placement="top">
              <Box sx={{ cursor: "help" }}>
                <Stat
                  label="Overdue"
                  value={totals.Overdue}
                  color={
                    totals.Overdue > 0
                      ? theme.palette.error.main
                      : theme.palette.text.secondary
                  }
                />
              </Box>
            </Tooltip>
          </Stack>
          {(stats.Breakdown.CarriedOverBacklog > 0 ||
            stats.Breakdown.UnscheduledCount > 0) && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
              {stats.Breakdown.CarriedOverBacklog > 0 && (
                <Tooltip title={BACKLOG_TOOLTIP} arrow placement="top">
                  <Chip
                    icon={<WarningIcon />}
                    label={`${stats.Breakdown.CarriedOverBacklog} backlog`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              {stats.Breakdown.UnscheduledCount > 0 && (
                <Chip
                  icon={<ScheduleIcon />}
                  label={`${stats.Breakdown.UnscheduledCount} utan datum`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Box>

        {/* Segmentuppdelning */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Per segment
        </Typography>
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <SegmentDonutChart breakdown={stats.Breakdown} height={260} />
          <Divider sx={{ my: 1.5 }} />
          {SEGMENT_ORDER.filter(
            (key) => getSegmentTotal(stats.Breakdown, key).Total > 0
          ).map((key, index, arr) => (
            <React.Fragment key={key}>
              <SegmentSummaryRow segmentKey={key} stats={stats} />
              {index < arr.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>

        {/* Subtypsfördelning */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Per aktivitetstyp
        </Typography>
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          {/* Planering (Communication) — intent / planeringsobjekt.
               Hålls separat från Mötesbokning för att inte mixa "planerat
               fysiskt möte" med "bokat fysiskt möte". */}
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
          >
            {CATEGORY_LABELS.Communication}
          </Typography>
          <Box sx={{ mt: 1, mb: 2.5 }}>
            <SubtypeProgressList
              subtypeBreakdown={stats.Breakdown.Total.Communication}
              subtypeOrder={SUBTYPE_ORDER_COMMUNICATION}
            />
            {stats.Breakdown.Total.Communication.Total.Total === 0 && (
              <Typography variant="body2" color="text.secondary">
                Inga planerade aktiviteter
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Mötesbokning (Followup) — konkret bokade aktiviteter. */}
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: "0.08em", mt: 1 }}
          >
            {CATEGORY_LABELS.Followup}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <SubtypeProgressList
              subtypeBreakdown={stats.Breakdown.Total.Followup}
              subtypeOrder={SUBTYPE_ORDER_FOLLOWUP}
            />
            {stats.Breakdown.Total.Followup.Total.Total === 0 && (
              <Typography variant="body2" color="text.secondary">
                Inga mötesbokningar
              </Typography>
            )}
          </Box>

          {/* Kontaktförsök — fristående effort-metrik, ej del av totalerna */}
          {getContactAttemptCount(stats.Breakdown.Total.Total) > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha("#9B6B00", 0.06),
                  border: `1px dashed ${alpha("#9B6B00", 0.4)}`,
                }}
              >
                <Box sx={{ color: "#9B6B00", display: "flex" }}>
                  {getWorkItemIcon(WorkItemTypeEnum.ContactAttempt)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Kontaktförsök
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ansträngning att nå kunden — ingår inte i totalen
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: "#9B6B00" }}>
                  {getContactAttemptCount(stats.Breakdown.Total.Total)}
                </Typography>
              </Stack>
            </>
          )}
        </Box>

        {/* Senaste aktiviteter */}
        {stats.RecentActivities.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Senaste aktiviteter
            </Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              {stats.RecentActivities.slice(0, 8).map((activity) => (
                <Box
                  key={activity.Id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[100], 0.4),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Box sx={{ color: getWorkItemColor(activity.Type) }}>
                    {getWorkItemIcon(activity.Type)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {activity.Name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {getWorkItemTypeName(activity.Type)}
                      </Typography>
                      {activity.CustomerName && (
                        <Typography variant="caption" color="text.secondary">
                          · {activity.CustomerName}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Tooltip
                    title={
                      activity.ScheduledTo
                        ? format(parseISO(activity.ScheduledTo), "PPpp", {
                            locale: sv,
                          })
                        : "Ej schemalagd"
                    }
                  >
                    <Typography variant="caption" color="text.secondary">
                      {activity.ScheduledTo
                        ? format(parseISO(activity.ScheduledTo), "d MMM", {
                            locale: sv,
                          })
                        : "—"}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {onOpenFullProfile && (
          <Button
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => onOpenFullProfile(stats.SalespersonId)}
          >
            Öppna säljarens aktivitetsvy
          </Button>
        )}
      </Box>
    </Drawer>
  );
};

const Stat: React.FC<{ label: string; value: number; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <Box>
    <Typography
      variant="h6"
      fontWeight="bold"
      sx={{ color: color, lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default SalespersonDetailDrawer;
